import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  CircularProgress,
  Typography,
  IconButton,
  TablePagination,
  TextField,
  MenuItem,
  Grid,
  Button,
  Tooltip,
  Popover,
} from "@mui/material";

import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import RefreshIcon from "@mui/icons-material/Refresh";
import { apiServices } from "../../../services/apiServices";
import Swal from "sweetalert2";


const AuditLog = () => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [filters, setFilters] = useState({
    search: "",
    country: "",
    status: "",
    startDate: "",
    endDate: "",
  });

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Register user (for Completed status – call users API)
  const [registeringLogId, setRegisteringLogId] = useState(null);

  // ✅ Fetch API
  useEffect(() => {
    const fetchAuditLogs = async () => {
      try {
        setLoading(true);

        const result = await apiServices.getAPI("failure-and-retry-logs");

        // If using axios-style response
        const data = result?.data;

        if (data?.success && data?.data) {
          const failure = data.data.failureLogs || [];
          const completed = data.data.completedAfterFailure || [];

          const getLogDate = (log) => log.created_at ?? log.created;
          const mergedLogs = [...failure, ...completed].sort(
            (a, b) => new Date(getLogDate(b)) - new Date(getLogDate(a))
          );

          setAuditLogs(mergedLogs);
        } else {
          setAuditLogs([]);
        }
      } catch (err) {
        console.error("Audit log fetch error:", err);
        setError("Failed to load audit logs.");
      } finally {
        setLoading(false);
      }
    };

    fetchAuditLogs();
  }, []);
  useEffect(() => {
    setPage(0);
  }, [filters]);

  const countryOptions = useMemo(() => {
    const countries = auditLogs.map((log) => log.country);
    return [...new Set(countries.filter(Boolean))];
  }, [auditLogs]);

  // ✅ Updated Filter Logic (Date Range)
  const filteredLogs = useMemo(() => {
    let filtered = [...auditLogs];

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter((log) =>
        log.username?.toLowerCase().includes(searchLower) ||
        log.email?.toLowerCase().includes(searchLower) ||
        log.payment_intent_id?.toLowerCase().includes(searchLower)
      );
    }

    if (filters.country) {
      filtered = filtered.filter(
        (log) => log.country === filters.country
      );
    }

    if (filters.status) {
      filtered = filtered.filter(
        (log) =>
          log.status?.toLowerCase() ===
          filters.status.toLowerCase()
      );
    }

    if (filters.startDate && filters.endDate) {
      const start = new Date(filters.startDate);
      const end = new Date(filters.endDate);
      end.setHours(23, 59, 59, 999);

      filtered = filtered.filter((log) => {
        const created = new Date(log.created_at ?? log.created);
        return created >= start && created <= end;
      });
    }

    return filtered;
  }, [auditLogs, filters]);

  const paginatedLogs = filteredLogs.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleRegisterUser = async (log) => {
    const id = log.payment_intent_id || log.id || `${log.email}-${log.created_at ?? log.created}`;
    setRegisteringLogId(id);
    try {
      const credentialCheckResponse = await apiServices.postAPI("check-credentials", {
        email: log.email,
        mobile: log.mobile || "",
      });
      const { summary } = credentialCheckResponse.data || {};
      if (summary && summary.emailExists) {
        await Swal.fire({
          icon: "warning",
          title: "Already registered",
          text: `Email "${log.email}" is already registered.`,
          confirmButtonColor: "#214F7D",
        });
        return;
      }
      const response = await apiServices.postAPI("users", {
        dob: log.dob,
        country: log.country,
        gender: log.gender,
        name: log.username,
        phone: log.mobile,
        reference_by: log.reference_by,
        payment_status: "COMPLETED",
        activestatus: "Y",
        mobile_email_flag: "Y",
        email: log.email,
      });
      if (response.data && (response.data.success || response.data.peacekeeper_id)) {
        await Swal.fire({
          icon: "success",
          title: "User registered",
          text: response.data?.message || `User has been registered successfully.${response.data?.peacekeeper_id ? ` Peacekeeper ID: ${response.data.peacekeeper_id}` : ""}`,
          confirmButtonColor: "#214F7D",
        });
      } else {
        throw new Error(response.data?.message || "Registration failed");
      }
    } catch (err) {
      console.error("Error registering user:", err);
      Swal.fire({
        icon: "error",
        title: "Registration failed",
        text: err.response?.data?.message || "Failed to register user. Please try again.",
      });
    } finally {
      setRegisteringLogId(null);
    }
  };

  return (
<Box sx={{ width: "100%", px: 3, py: 2}}>      
      <Paper sx={{  mb: 3 }}>
        <Grid container spacing={2} alignItems="center">

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              placeholder="Username, Email, Payment ID"
              value={filters.search}
              onChange={(e) =>
                setFilters({ ...filters, search: e.target.value })
              }
            />
          </Grid>

          <Grid item xs={12} md={2} sx={{ width: 100 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Country"
              value={filters.country}
              onChange={(e) =>
                setFilters({ ...filters, country: e.target.value })
              }
            >
              <MenuItem value="">All</MenuItem>
              {countryOptions.map((country, i) => (
                <MenuItem key={i} value={country}>
                  {country}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={2} sx={{ width: 100 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Status"
              value={filters.status}
              onChange={(e) =>
                setFilters({ ...filters, status: e.target.value })
              }
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Failure">Failure</MenuItem>
              <MenuItem value="Completed">Completed</MenuItem>
            </TextField>
          </Grid>

          {/* DATE RANGE BUTTON */}
          <Grid item xs={12} md={3}>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<CalendarTodayIcon />}
              onClick={(e) => setAnchorEl(e.currentTarget)}
            >
              {filters.startDate && filters.endDate
                ? `${filters.startDate} - ${filters.endDate}`
                : "Select date range"}
            </Button>

            <Popover
              open={open}
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
            >
              <Box p={3} width={300}>
                <Typography mb={2}>
                  Select Date Range
                </Typography>

                <TextField
                  type="date"
                  label="Start Date"
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={filters.startDate}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      startDate: e.target.value,
                    })
                  }
                  sx={{ mb: 2 }}
                />

                <TextField
                  type="date"
                  label="End Date"
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={filters.endDate}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      endDate: e.target.value,
                    })
                  }
                />
              </Box>
            </Popover>
          </Grid>
          <Grid item xs="auto">
            <Tooltip title="Reset Filters">
              <IconButton
                onClick={() =>
                  setFilters({
                    search: "",
                    country: "",
                    status: "",
                    startDate: "",
                    endDate: "",
                  })
                }
                sx={{
                  border: "1px solid #ddd",
                  borderRadius: 2,
                  height: 40,
                  width: 40,
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Grid>

        </Grid>
      </Paper>

      {/* ✅ TABLE */}
      {loading && <CircularProgress />}
      {error && <Typography color="error">{error}</Typography>}

      {!loading && !error && (
       <Paper elevation={3} sx={{ borderRadius: 3}}>
  <TableContainer sx={{ maxHeight: 350, overflowX: 'auto', overflowY: 'auto' }}>
            <Table stickyHeader size="small" sx={{ minWidth: 1400 }}>
              <TableHead>
                <TableRow>
                  {[
                    "Username",
                    "Email",
                    "Country",
                    "Amount",
                    "Status",
                    "Failure Reason",
                    "Cancellation Reason",
                    "Payment Intent ID",
                    "Created At",
                    "Actions",
                  ].map((head) => (
                    <TableCell
                      key={head}
                      sx={{
                        fontWeight: 600,
                        backgroundColor: "#f4f6f8",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {head}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedLogs.map((log, index) => {
                  const isFailure =
                    log.status?.toLowerCase() === "failure";

                  return (
                    <TableRow key={index} hover>
                      <TableCell>{log.username}</TableCell>
                      <TableCell>{log.email}</TableCell>
                      <TableCell>{log.country}</TableCell>
                      <TableCell>{log.amount}</TableCell>

                      <TableCell>
                        <Box
                          sx={{
                            display: "inline-block",
                            borderRadius: 2,
                            fontSize: 12,
                            fontWeight: 600,
                            backgroundColor: isFailure
                              ? "#fdecea"
                              : "#e8f5e9",
                            color: isFailure
                              ? "#d32f2f"
                              : "#2e7d32",
                          }}
                        >
                          {log.status}
                        </Box>
                      </TableCell>

                      <TableCell>{log.failure_reason}</TableCell>
                      <TableCell>{log.cancellation_reason}</TableCell>
                      <TableCell>{log.payment_intent_id}</TableCell>
                      <TableCell>
                        {new Date(log.created_at ?? log.created).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        {log.status?.toLowerCase() === "completed" && (
                          <span>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handleRegisterUser(log)}
                              disabled={registeringLogId !== null}
                              sx={{
                                minWidth: 100,
                                backgroundColor: "#214F7D",
                                "&:hover": { backgroundColor: "#1a3d5c" },
                              }}
                            >
                              {registeringLogId === (log.payment_intent_id || log.id || `${log.email}-${log.created_at ?? log.created}`)
                                ? "Registering..."
                                : "Register"}
                            </Button>
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
             <Box sx={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #E0E0E0' }}>
           
          </Box>
          </TableContainer>
           <TablePagination
              component="div"
              count={filteredLogs.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
            />
        </Paper>
      )}
    </Box>
  );
};
export default AuditLog;
